import type { WasteReportSummary, WasteReviewInfo } from '../types/waste';

// F2 · lógica PURA de «Por revisar» y del reporte de mermas (sin renderizador, con pruebas). Las fechas
// viajan como días «AAAA-MM-DD»; el servidor las convierte con la zona de cada tienda.

export const MAX_WASTE_RANGE_DAYS = 92;

const money = (value: string | number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value));

/** «AAAA-MM-DD» de un momento en una zona (la del teléfono si no se da). */
export function localDateString(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

/** Suma (o resta) días a un «AAAA-MM-DD», al mediodía UTC para que ningún cambio de horario lo mueva. */
export function shiftLocalDate(iso: string, days: number): string {
  const date = new Date(`${iso}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Los últimos `days` días, hoy incluido. */
export function defaultRange(days: number, now: Date = new Date(), timeZone?: string): { dateFrom: string; dateTo: string } {
  const dateTo = localDateString(now, timeZone);
  return { dateFrom: shiftLocalDate(dateTo, -(days - 1)), dateTo };
}

/** Por qué no se puede consultar el rango (null = listo). Las mismas reglas que el servidor. */
export function rangeProblem(dateFrom: string, dateTo: string): string | null {
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  if (!iso.test(dateFrom) || !iso.test(dateTo)) return 'Elige las dos fechas.';
  if (dateFrom > dateTo) return 'La fecha inicial no puede ser posterior a la final.';
  const days = (Date.parse(`${dateTo}T00:00:00Z`) - Date.parse(`${dateFrom}T00:00:00Z`)) / 86_400_000;
  if (days > MAX_WASTE_RANGE_DAYS) return 'Elige un rango de hasta 3 meses.';
  return null;
}

/** «12 sep, 18:30» en la zona de la TIENDA donde pasó. */
export function formatWasteDateTime(occurredAt: string, timeZone: string): string {
  return new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(occurredAt));
}

/** «Revisada por Gerente Uno · 12 sep, 18:30 · Nota: …» */
export function reviewedText(review: WasteReviewInfo, timeZone: string): string {
  const parts = [`Revisada por ${review.reviewedBy?.name ?? 'un gerente'}`, formatWasteDateTime(review.reviewedAt, timeZone)];
  if (review.note) parts.push(`Nota: ${review.note}`);
  return parts.join(' · ');
}

/** «3 mermas · $120.00 · 1 sin costo registrado» */
export function reportSummaryText(summary: WasteReportSummary): string {
  const parts = [`${summary.count} ${summary.count === 1 ? 'merma' : 'mermas'}`, money(summary.value)];
  if (summary.uncostedCount) parts.push(`${summary.uncostedCount} sin costo registrado`);
  return parts.join(' · ');
}
